import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaSave, FaArrowLeft, FaUsers } from 'react-icons/fa';
import { toast } from "react-toastify";
import { updateTeacher, clearState, getAllTeachers } from '../redux/Slices/TeachersSlice';
import { getStudyMaterials } from '../redux/Slices/StudyMaterialsSlice';
import { fetchGrades } from '../redux/Slices/GradesSlice';
import { validateFieldLanguage } from '../Utils/languageValidation';

const EditTeacher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    status: 1,
    subject_id: '',
    grades: []
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { loading, error, success, all_teachers } = useSelector((state) => state.teachers);
  const { studyMaterials: subjects } = useSelector((state) => state.studyMaterials);
  const { grades } = useSelector((state) => state.grades);

  useEffect(() => {
    dispatch(clearState());
    dispatch(getStudyMaterials());
    dispatch(fetchGrades());
    dispatch(getAllTeachers());
    
    const teacherData = sessionStorage.getItem(`teacher_${id}`);
    if (teacherData) {
      const teacher = JSON.parse(teacherData);
      const original = {
        name_en: teacher.name_en || '',
        name_ar: teacher.name_ar || '',
        status: teacher.status || 1,
        subject_id: teacher.subject_id ? String(teacher.subject_id) : '',
        grades: teacher.grades ? teacher.grades.map(g => String(g)) : []
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (originalData) {
      const hasFormChanges = 
      formData.name_en !== originalData.name_en ||
      formData.name_ar !== originalData.name_ar ||
      formData.status !== originalData.status ||
      formData.subject_id !== originalData.subject_id ||
      JSON.stringify(formData.grades.sort()) !== JSON.stringify(originalData.grades.sort());
      
      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (success) {
      toast.success(success || "تم تحديث المعلم بنجاح");
      dispatch(clearState());
      navigate('/teachers');
    }
    if (error) {
      toast.error(typeof error === 'string' ? error : "فشل في تحديث المعلم");
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? parseInt(value) : value
    }));
  };

  const handleMultiSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearState());
    
    if (!formData.name_en.trim()) {
      toast.error('يرجى إدخال اسم المعلم بالإنجليزية');
      return;
    }
    
    if (!formData.name_ar.trim()) {
      toast.error('يرجى إدخال اسم المعلم بالعربية');
      return;
    }
    
    if (!formData.subject_id) {
      toast.error('يرجى اختيار المادة');
      return;
    }

    // التحقق من لغة الحقول (منع خلط العربي بالإنجليزي)
    const arError = validateFieldLanguage(formData.name_ar, "ar");
    if (arError) return toast.error(arError);
    const enError = validateFieldLanguage(formData.name_en, "en");
    if (enError) return toast.error(enError);

    // التحقق من عدم تكرار اسم المعلم (عربي أو إنجليزي) باستثناء المعلم الحالي
    const isDuplicateAr = Array.isArray(all_teachers) && all_teachers.some(
      (t) => t.id !== parseInt(id) && t.name_ar.trim().toLowerCase() === formData.name_ar.trim().toLowerCase()
    );
    if (isDuplicateAr) {
      toast.error("اسم المعلم بالعربي مكرر بالفعل");
      return;
    }

    const isDuplicateEn = Array.isArray(all_teachers) && all_teachers.some(
      (t) => t.id !== parseInt(id) && t.name_en.trim().toLowerCase() === formData.name_en.trim().toLowerCase()
    );
    if (isDuplicateEn) {
      toast.error("اسم المعلم بالإنجليزي مكرر بالفعل");
      return;
    }

    dispatch(updateTeacher({ id, teacherData: formData }));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--warning-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(251, 191, 36, 0.15)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .teach-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .teach-custom-select:focus {
          border-color: var(--warning-color) !important;
          box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15) !important;
        }
        .teach-multi-select {
          width: 100% !important;
          padding: 10px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          min-height: 120px !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .teach-multi-select:focus {
          border-color: var(--warning-color) !important;
          box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <FaUsers style={{ color: "var(--gray-color)" }} /> تعديل المعلم
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل بيانات المعلم المحددة وتحديث المادة المسندة والصفوف الدراسية</p>
        </div>
        <Link to="/teachers" style={{ textDecoration: "none" }}>
          <button style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            <FaArrowLeft size={11} /> العودة
          </button>
        </Link>
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>اسم المعلم (العربية) *</label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل اسم المعلم بالعربية"
                required
              />
            </div>

            <div>
              <label style={labelStyle}>اسم المعلم (الإنجليزية) *</label>
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل اسم المعلم بالإنجليزية"
                required
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>المادة *</label>
              <select
                className="teach-custom-select"
                name="subject_id"
                value={formData.subject_id}
                onChange={handleInputChange}
                required
              >
                <option value="">اختر المادة</option>
                {subjects?.map((subject) => (
                  <option key={subject.id} value={String(subject.id)}>
                    {subject.name_ar}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>الحالة *</label>
              <select
                className="teach-custom-select"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
              >
                <option value={1}>🟢 نشط</option>
                <option value={0}>🔴 غير نشط</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>الصفوف المسؤولة</label>
            <select
              className="teach-multi-select"
              name="grades"
              multiple
              value={formData.grades}
              onChange={(e) => {
                const selectedGrades = Array.from(e.target.selectedOptions, option => String(option.value));
                handleMultiSelectChange('grades', selectedGrades);
              }}
            >
              {grades?.data?.map((grade) => (
                <option key={grade.id} value={String(grade.id)}>
                  {grade.name_ar}
                </option>
              ))}
            </select>
            <small style={{ display: "block", marginTop: 6, color: "#94a3b8", fontSize: 11, textAlign: "right" }}>💻 اضغط Ctrl (أو Command في Mac) لاختيار أكثر من صف دراسي.</small>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate("/teachers")}
              style={{ padding: "10px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading || !hasChanges}
              style={{
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: (loading || !hasChanges) ? "#cbd5e1" : "linear-gradient(135deg,#f59e0b,var(--warning-color))",
                color: "#fff",
                fontWeight: 700,
                cursor: (loading || !hasChanges) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  جاري التحديث...
                </>
              ) : (
                <>
                  <FaSave size={12} />
                  تحديث المعلم
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeacher;
